import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, firstValueFrom, throwError } from 'rxjs';
import { applicationConfiguration, AppConfig } from '../interceptor/app.config';
import { secureStorage } from './secure-storage.service';
import { canPlaceOrder } from '../utils/gst-eligibility';

export type DocumentPresignedType =
  | 'tax_invoice'
  | 'booking'
  | 'credit_note'
  | 'proforma';

export interface DocumentPresignedUrlRequest {
  document_type: DocumentPresignedType;
  invoice_number?: string;
  booking_number?: string;
  credit_note_number?: string;
  expires_in: number;
}

export interface DocumentPresignedUrlData {
  invoice_number?: string;
  booking_number?: string;
  credit_note_number?: string;
}

export interface SmsInvoiceDownloadPayload {
  short_code: string;
}

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  protected server = this.appConfig.apiUrl + 'api/eol/v1/pv/';
  protected server2 = this.appConfig.apiUrl + 'api/eol/';
  private dataSubject = new BehaviorSubject<any>(null);
  currentData = this.dataSubject.asObservable();

  private setBillingAddress = new BehaviorSubject<any>(null);
  currentBillingAddress = this.setBillingAddress.asObservable();

  private setShippingAddress = new BehaviorSubject<any>(null);
  currentShippingAddress = this.setShippingAddress.asObservable();

  readonly options = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
    }),
  };

  /** Plain client for MITM public key — bypasses HTTP interceptors to avoid recursion. */
  private readonly mitmPlainHttp: HttpClient;
  private mitmCachedPublicKey?: string;
  private mitmFetchingPublicKey?: Promise<string>;

  constructor(
    private http: HttpClient,
    httpBackend: HttpBackend,
    private localstorage: secureStorage,
    @Inject(applicationConfiguration) private appConfig: AppConfig,
  ) {
    this.mitmPlainHttp = new HttpClient(httpBackend);
  }

  /**
   * POST public endpoint: server ECDSA public key for response signature verification.
   * Cached in memory for the session.
   */
  getMitmServerPublicKey(): Promise<string> {
    if (this.mitmCachedPublicKey) {
      return Promise.resolve(this.mitmCachedPublicKey);
    }
    if (this.mitmFetchingPublicKey) {
      return this.mitmFetchingPublicKey;
    }
    const url = `${this.server}/common/get_public_key/`;
    const headers = new HttpHeaders({
      Accept: 'application/json',
      'Content-Type': 'application/json',
    });
    this.mitmFetchingPublicKey = firstValueFrom(
      this.mitmPlainHttp.post<{
        success?: boolean;
        status?: boolean;
        data?: { msg?: string; public_key?: string };
      }>(url, {}, { headers }),
    )
      .then((res) => {
        const key = res?.data?.public_key;
        if (!key || typeof key !== 'string') {
          this.mitmFetchingPublicKey = undefined;
          throw new Error('Invalid public key response');
        }
        this.mitmCachedPublicKey = key;
        this.mitmFetchingPublicKey = undefined;
        return this.mitmCachedPublicKey;
      })
      .catch((err) => {
        this.mitmFetchingPublicKey = undefined;
        throw err;
      });
    return this.mitmFetchingPublicKey;
  }

  clearMitmServerPublicKeyCache(): void {
    this.mitmCachedPublicKey = undefined;
    this.mitmFetchingPublicKey = undefined;
  }
  getUserDetails(payload: any): Observable<any> {
    return this.http.post(
      `${this.server}account/user_profile/`,
      payload,
      this.options,
    );
  }

  updateAdress(product: any): Observable<any> {
    return this.http.post(
      `${this.server}account/update_default_address/`,
      product,
      this.options,
    );
  }

  bannerList(product: any): Observable<any> {
    return this.http.post(
      `${this.server}account/get_banner_list/`,
      product,
      this.options,
    );
  }

  guideSearch(product: any): Observable<any> {
    return this.http.post(
      `${this.server}search/get_guided_search/`,
      product,
      this.options,
    );
  }

  createMostSearchPart(product: any): Observable<any> {
    return this.http.post(
      `${this.server}account/create_most_search_part/`,
      product,
      this.options,
    );
  }

  getFilters(product: any): Observable<any> {
    return this.http.post(
      `${this.server}common/get_filter_dropdown/`,
      product,
      this.options,
    );
  }

  sendOtp(product: any): Observable<any> {
    return this.http.post(
      `${this.server}account/send_otp/`,
      product,
      this.options,
    );
  }

  verifyOtp(product: any): Observable<any> {
    return this.http.post(
      `${this.server}account/login_verify_otp/`,
      product,
      this.options,
    );
  }

  userRegistration(payload: any): Observable<any> {
    return this.http.post(
      `${this.server}account/user_registration/`,
      payload,
      this.options,
    );
  }

  getProductlistData(payload: any): Observable<any> {
    return this.http.post(
      `${this.server}search/part_search/`,
      payload,
      this.options,
    );
  }

  getProductDetailData(payload: any): Observable<any> {
    return this.http.post(
      `${this.server}search/part_detail/`,
      payload,
      this.options,
    );
  }

  addToCart(payload: any): Observable<any> {
    return this.http.post(`${this.server}cart/cart/`, payload, this.options);
  }

  getCartData(payload: any): Observable<any> {
    return this.http.post(`${this.server}cart/cart/`, payload, this.options);
  }

  getBuyNowPriceBreakup(payload: any): Observable<any> {
    return this.http.post(
      `${this.server}cart/get_buy_now_price_breakup/`,
      payload,
      this.options,
    );
  }
  removeCartData(payload: any): Observable<any> {
    return this.http.post(
      `${this.server}cart/remove_cart/`,
      payload,
      this.options,
    );
  }

  addToWishlist(payload: any): Observable<any> {
    return this.http.post(
      `${this.server}cart/wishlist/`,
      payload,
      this.options,
    );
  }

  getWishlistData(payload: any): Observable<any> {
    return this.http.post(
      `${this.server}cart/wishlist/`,
      payload,
      this.options,
    );
  }

  updateSendOtp(payload: any): Observable<any> {
    return this.http.post(
      `${this.server}account/update_send_otp/`,
      payload,
      this.options,
    );
  }

  updateVerifyOtp(payload: any): Observable<any> {
    return this.http.post(
      `${this.server}account/update_user_verify_otp/`,
      payload,
      this.options,
    );
  }

  getAddressData(payload: any): Observable<any> {
    return this.http.post(
      `${this.server}account/user_address_list/`,
      payload,
      this.options,
    );
  }

  addAddress(payload: any): Observable<any> {
    return this.http.post(
      `${this.server}account/add_user_address/`,
      payload,
      this.options,
    );
  }

  getGstdata(payload: any): Observable<any> {
    return this.http.post(
      `${this.server}account/get_gst_details/`,
      payload,
      this.options,
    );
  }

  addGstdata(payload: any): Observable<any> {
    return this.http.post(
      `${this.server}account/add_user_address/`,
      payload,
      this.options,
    );
  }

  updateAddressdata(payload: any): Observable<any> {
    return this.http.post(
      `${this.server}account/update_default_address/`,
      payload,
      this.options,
    );
  }

  dataBypincode(payload: any): Observable<any> {
    return this.http.post(
      `${this.server}search/geo_dropdowns/`,
      payload,
      this.options,
    );
  }

  geoLocation(payload: any): Observable<any> {
    return this.http.post(
      `${this.server}search/get_pincode_by_lat_lon/`,
      payload,
      this.options,
    );
  }

  getadvancePartSearch(payload: any): Observable<any> {
    return this.http.post(
      `${this.server}search/advance_part_search/`,
      payload,
      this.options,
    );
  }

  buyNow(payload: any): Observable<any> {
    const rawUser = localStorage.getItem('userData1');
    if (rawUser) {
      try {
        const parsed = JSON.parse(rawUser);
        if (!canPlaceOrder(parsed)) {
          return throwError(
            () =>
              new Error(
                'Please update your profile to complete business verification before placing an order.',
              ),
          );
        }
      } catch (_) { }
    }

    // For FormData, don't set Content-Type - let browser set it with boundary
    const options =
      payload instanceof FormData
        ? { headers: new HttpHeaders() } // Empty headers for FormData
        : { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) };

    return this.http.post(
      `${this.server}order/create_order/`,
      payload,
      options,
    );
  }

  cancelOrder(payload: any): Observable<any> {
    return this.http.post(
      `${this.server}order/booking_cancellation_request/`,
      payload,
      this.options,
    );
  }

  ratingOrder(payload: any): Observable<any> {
    return this.http.post(
      `${this.server}order/invoice_feedback/`,
      payload,
      this.options,
    );
  }

  orderInvoiceprice(payload: any): Observable<any> {
    return this.http.post(
      `${this.server}order/get_order_address_n_pricing/`,
      payload,
      this.options,
    );
  }

  faqList(payload: any): Observable<any> {
    return this.http.post(
      `${this.server}common/get_faq_list/`,
      payload,
      this.options,
    );
  }

  getRecentlyViewedProductlistData(payload: any): Observable<any> {
    return this.http.post(
      `${this.server}cart/recently_viewed_items/`,
      payload,
      this.options,
    );
  }

  customerFeddback(payload: any): Observable<any> {
    return this.http.post(
      `${this.server}account/get_customer_rating/`,
      payload,
      this.options,
    );
  }

  enquiryNow(payload: any): Observable<any> {
    // If payload is FormData, don't set Content-Type header (let browser set it automatically)
    if (payload instanceof FormData) {
      return this.http.post(`${this.server}account/create_enquiry/`, payload);
    }
    // For regular JSON payloads, use the default options
    return this.http.post(
      `${this.server}account/create_enquiry/`,
      payload,
      this.options,
    );
  }

  getBlogs(payload: any): Observable<any> {
    return this.http.post(
      `${this.server}admins/get_blog_list/`,
      payload,
      this.options,
    );
  }

  getBrand(payload: any): Observable<any> {
    return this.http.post(
      `${this.server}common/vehicle_mapping_dropdowns/`,
      payload,
      this.options,
    );
  }

  getVehicle(payload: any): Observable<any> {
    return this.http.post(
      `${this.server}common/vehicle_mapping_dropdowns/`,
      payload,
      this.options,
    );
  }

  AddVehicle(payload: any): Observable<any> {
    return this.http.post(
      `${this.server}common/vehicle_mapping_dropdowns/`,
      payload,
      this.options,
    );
  }

  createGarage(payload: any): Observable<any> {
    return this.http.post(
      `${this.server}account/create_garage/`,
      payload,
      this.options,
    );
  }
  get_garage(payload: any): Observable<any> {
    return this.http.post(
      `${this.server}account/get_garage/`,
      payload,
      this.options,
    );
  }

  getorderlist(payload: any): Observable<any> {
    return this.http.post(
      `${this.server}order/invoice/`,
      payload,
      this.options,
    );
  }

  getBookingData(payload: any): Observable<any> {
    return this.http.post(
      `${this.server}order/get_booking_data/`,
      payload,
      this.options,
    );
  }

  getPendingBookingAmount(payload: any): Observable<any> {
    return this.http.post(
      `${this.server}order/pending_booking_amount/`,
      payload,
      this.options,
    );

  }

  get_category(payload: any): Observable<any> {
    return this.http.post(
      `${this.server}account/get_category/`,
      payload,
      this.options,
    );
  }

  getModelSearch(payload: any): Observable<any> {
    return this.http.post(
      `${this.server}account/get_most_search_part/`,
      payload,
      this.options,
    );
  }

  addModelSearch(payload: any): Observable<any> {
    return this.http.post(
      `${this.server}account/create_most_search_part/`,
      payload,
      this.options,
    );
  }

  user_logout(payload: any): Observable<any> {
    return this.http.post(
      `${this.server}account/user_logout/`,
      payload,
      this.options,
    );
  }
  getOrderDetail(payload: any): Observable<any> {
    return this.http.post(
      `${this.server}order/order_details/`,
      payload,
      this.options,
    );
  }

  requestCallBack(payload: any): Observable<any> {
    return this.http.post(
      `${this.server}order/customer_support/`,
      payload,
      this.options,
    );
  }

  getFaqData(payload: any): Observable<any> {
    return this.http.post(
      `${this.server}common/get_faq_list/`,
      payload,
      this.options,
    );
  }

  submitReturnRequest(payload: any): Observable<any> {
    return this.http.post(
      `${this.server}order/return_request/`,
      payload,
      this.options,
    );
  }

  add_scrap_certificate(formData: FormData): Observable<any> {
    // For FormData, we need to remove the default headers and let the browser set Content-Type
    const formDataOptions = {
      headers: {
        // Don't set Content-Type, let browser set it with boundary for multipart/form-data
      },
    };
    return this.http.post(
      `${this.server}account/add_scrap_certificate/`,
      formData,
      formDataOptions,
    );
  }

  getScrapCertificateList(payload: any): Observable<any> {
    return this.http.post(
      `${this.server}account/get_scrap_certificates/`,
      payload,
      this.options,
    );
  }

  checkScrapCategoryMatch(payload: any): Observable<any> {
    return this.http.post(
      `${this.server}account/get_scrap_certificates/`,
      payload,
      this.options,
    );
  }

  uploadReturnImage(payload: any): Observable<any> {
    // If payload is FormData, don't set Content-Type header (let browser set it automatically)
    if (payload instanceof FormData) {
      return this.http.post(
        `${this.server}order/upload_return_images/`,
        payload,
      );
    }
    // For regular JSON payloads, use the default options
    return this.http.post(
      `${this.server}order/upload_return_images/`,
      payload,
      this.options,
    );
  }

  orderListDetails(payload: any): Observable<any> {
    return this.http.post(`${this.server}order/tat/`, payload, this.options);
  }

  loginByPass(payload: any): Observable<any> {
    return this.http.post(
      `${this.server}account/login_request_accept/`,
      payload,
      this.options,
    );
  }

  getBuyerBestOffer(payload: any): Observable<any> {
    return this.http.post(
      `${this.server}cart/buyer_best_offer/`,
      payload,
      this.options,
    );
  }


  buildDocumentPresignedUrlPayload(
    documentType: DocumentPresignedType,
    data: DocumentPresignedUrlData,
  ): DocumentPresignedUrlRequest {
    switch (documentType) {
      case 'tax_invoice':
        return {
          document_type: 'tax_invoice',
          invoice_number: data?.invoice_number?.trim() || '',
          expires_in: 900,
        };
      case 'booking':
        return {
          document_type: 'booking',
          booking_number: data?.booking_number?.trim() || '',
          expires_in: 900,
        };
      case 'credit_note':
        return {
          document_type: 'credit_note',
          invoice_number: data?.invoice_number?.trim() || '',
          credit_note_number: data?.credit_note_number?.trim() || '',
          expires_in: 900,
        };
      case 'proforma':
        return {
          document_type: 'proforma',
          booking_number: data?.booking_number?.trim() || '',
          expires_in: 900,
        };
    }
  }

  /**
   * Request a presigned document URL.
   * Endpoint: POST /api/eol/v1/pv/order/document_presigned_url/
   */
  documentPresignedUrl(
    payload: DocumentPresignedUrlRequest,
  ): Observable<any> {

    return this.http.post(
      `${this.server}order/document_presigned_url/`,
      payload,
      this.options,
    );
  }

  /**
   * Reusable helper to request presigned URL with dynamic payload for a specific document type.
   */
  getDocumentPresignedUrl(
    documentType: DocumentPresignedType,
    data: DocumentPresignedUrlData,
  ): Observable<any> {
    const payload = this.buildDocumentPresignedUrlPayload(documentType, data);
    return this.documentPresignedUrl(payload);
  }


  smsinvoiceDownload(
    payload: SmsInvoiceDownloadPayload | any,
  ): Observable<any> {
    return this.http.post(
      `${this.server}order/pdf_download/`,
      payload,
      this.options,
    );
  }
}
